import { makeAutoObservable, runInAction } from 'mobx'
import React from 'react'
import { currencyFormat as currencyFormatHelper, currencySymbol as currencySymbolHelper, penceToPounds } from '../currency'
import { plural } from '../lib/plural'

export const defaultCurrency = 'gbp' as SupportedCurrency
export const defaultCountry = 'GB' as SupportedCountry

export const SMS_COST = 7
export const EMAIL_COST = 0
export const STRIPE_FEE = '1.6% + 30p'

class I18n {
  language = ''
  languages: Record<string, Record<string, string>> = {}
  currency = defaultCurrency
  country = defaultCountry
  costSettings: Record<string, Record<string, number>> = {}
  locale = 'en-GB'

  constructor(languages: Record<string, Record<string, string>>, language: string) {
    this.languages = languages
    runInAction(() => {
      this.language = language
    })

    makeAutoObservable(this)
  }

  setLanguage(language: string) {
    this.language = language
  }

  get currentLanguage() {
    return this.languages[this.language]
  }

  setCurrency(currency: SupportedCurrency) {
    this.currency = currency
  }

  setCountry(country: SupportedCountry) {
    this.country = country
  }

  setCostSettings(costSettings: Record<string, any>) {
    this.costSettings = costSettings
  }

  setLocale(locale: string) {
    this.locale = locale
  }

  // Method to translate string
  s = (string: string, ...args: (string | number | null | undefined)[]) => {
    if (string === '£') return this.currencySymbol

    // Create lowercase version
    const stringL = string.toLowerCase()

    // Match on string, lowercased string or return initial string passed in
    let str = this.currentLanguage![string] || this.currentLanguage![stringL] || string

    // Do templating - wrap placeholders with delimiters to avoid conflicts
    const delimiter = '___PLACEHOLDER___'

    // First, wrap all placeholders with delimiters
    args.forEach((arg, index) => {
      str = str.replace(new RegExp(`\\$${index}`, 'g'), `${delimiter}${index}${delimiter}`)
    })

    // Then replace each wrapped placeholder with its value
    args.forEach((arg, index) => {
      str = str.replace(new RegExp(`${delimiter}${index}${delimiter}`, 'g'), `${arg}`)
    })

    return str
  }

  // Translate first, then inflect: `plural` is English-only, so a translated string is pluralised by
  // English rules whatever the locale. That was already true and is the reason this can't simply be
  // `plural(string, count)`.
  pluralise = (string: string, count = 0) => {
    const i18nString = this.s(string)

    if (count === 1) return i18nString

    return plural(i18nString)
  }

  intToOrdinal = (num: number): string => {
    num = Math.round(num)
    const numString = num.toString()

    if (this.locale === 'en-GB') {
      // If the ten's place is 1, the suffix is always "th"
      // (10th, 11th, 12th, 13th, 14th, 111th, 112th, etc.)
      if (Math.floor(num / 10) % 10 === 1) {
        return numString + 'th'
      }

      // Otherwise, the suffix depends on the one's place as follows
      // (1st, 2nd, 3rd, 4th, 21st, 22nd, etc.)
      switch (num % 10) {
        case 1: return numString + 'st'
        case 2: return numString + 'nd'
        case 3: return numString + 'rd'
        default: return numString + 'th'
      }
    }

    return numString
  }

  currencyFormat = (amount: number, options?: CurrencyOptions) => {
    const { fractionDigits = 2, isPennies = true, currency } = options || {}
    return currencyFormatHelper(amount, fractionDigits, isPennies, this.locale, currency || this.currency)
  }

  formatCurrencyForStats = (amount: number) => {
    const pounds = penceToPounds(amount)
    const symbol = this.currencySymbol

    if (pounds >= 1000) {
      if ((pounds / 1000) % 1 === 0) return symbol + `${pounds / 1000}k`

      return symbol + `${(pounds / 1000).toFixed(1)}k`
    }

    return symbol + `${pounds.toFixed(0)}`
  }

  get currencySymbol() {
    return currencySymbolHelper(this.locale, this.currency)
  }

  formattedCost = (feature: 'sms' | 'email' | 'stripe') => {
    if (this.costSettings && this.costSettings[feature]) {
      const cost = this.costSettings[feature]![this.country] || this.costSettings[feature]!['default']
      if (cost) return this.currencyFormat(cost)
    }

    if (feature === 'sms') {
      return this.currencyFormat(SMS_COST)
    }

    if (feature === 'stripe') {
      return STRIPE_FEE
    }

    return this.currencyFormat(EMAIL_COST)
  }
}

type CurrencyOptions = { fractionDigits?: number, isPennies?: boolean, currency?: SupportedCurrency | null }
export type $i18n = I18n
export default I18n

export const I18nContext = React.createContext({} as I18n)

export const useI18n = () => {
  const i18n = React.useContext(I18nContext)
  if (!i18n) {
    // this is especially useful in TypeScript so you don't need to be checking for null all the time
    throw new Error('i18n must be used within a I18nContext.')
  }
  return i18n
}